import _get from 'lodash.get';
import { normalizeAdyenCardBrand } from '../components/AdyenCreditCardForm/AdyenCreditCardForm.static.js';
import { defaultString } from '../utils/utils.js';

/*
 * Errors
 */
const DEFAULT_CARD_ERRORS = {
    encryptedCardNumber: null,
    encryptedExpiryDate: null,
    encryptedSecurityCode: null,
};
const DEFAULT_ERRORS = {
    ...DEFAULT_CARD_ERRORS,
    holderName: null,
};
/**
 * Validate the credit card holder name with the following rules:
 *  - The name cannot be empty
 *  - Length between 4 and 30 included
 *
 * @param holderName The string value that needs to be validated
 * @return True if the given name is valid, otherwise false
 */
const isHolderNameValid = (holderName) => {
    /* Regex explanation: String can contain any character except
     * linebreaks. Length between 4 and 30 characters included. */
    const regex = /^.{4,30}$/;
    return regex.test(holderName);
};
function maybeErrorForHolderName(holderName, t) {
    if (holderName === '') {
        return t('checkout.form.required.error');
    }
    else if (!isHolderNameValid(holderName)) {
        return t('checkout.payment.creditcard.name.invalid');
    }
    else {
        return null;
    }
}
function areIdenticalCardErrors(currentErrors, newErrors) {
    return (currentErrors.encryptedCardNumber === newErrors.encryptedCardNumber &&
        currentErrors.encryptedExpiryDate === newErrors.encryptedExpiryDate &&
        currentErrors.encryptedSecurityCode === newErrors.encryptedSecurityCode);
}
/*
 * Parsing Adyen's state in the 'onChange' callback
 */
const DEFAULT_ON_CHANGE_PAYMENT_METHOD = {
    type: '',
    encryptedCardNumber: '',
    encryptedExpiryMonth: '',
    encryptedExpiryYear: '',
    encryptedSecurityCode: '',
};
const DEFAULT_ADYEN_STATE = {
    isValid: false,
    storeInVault: false,
    creditCardBrand: null,
    lastFour: '',
    paymentMethod: {
        ...DEFAULT_ON_CHANGE_PAYMENT_METHOD,
        holderName: '',
    },
};
function getPaymentMethodField(stateOnChange, name) {
    return defaultString(_get(stateOnChange, `data.paymentMethod.${name}`));
}
function getErrorField(stateOnChange, name) {
    const value = _get(stateOnChange, `errors.${name}.errorI18n`);
    return typeof value === 'string' ? value : null;
}
// This extracts the data we need from the stateOnChange from Adyen, and uses to the appropriate default values for each field.
function parseStateOnChange(stateOnChange) {
    return {
        isValid: _get(stateOnChange, 'isValid', false),
        paymentMethod: {
            type: getPaymentMethodField(stateOnChange, 'type'),
            encryptedCardNumber: getPaymentMethodField(stateOnChange, 'encryptedCardNumber'),
            encryptedExpiryMonth: getPaymentMethodField(stateOnChange, 'encryptedExpiryMonth'),
            encryptedExpiryYear: getPaymentMethodField(stateOnChange, 'encryptedExpiryYear'),
            encryptedSecurityCode: getPaymentMethodField(stateOnChange, 'encryptedSecurityCode'),
        },
        errors: {
            encryptedCardNumber: getErrorField(stateOnChange, 'encryptedCardNumber'),
            encryptedSecurityCode: getErrorField(stateOnChange, 'encryptedSecurityCode'),
            // The month field and the date field are on the same height in the UI. Only one error message can be displayed.
            // => we pick the first non-null error, priority (arbitrarily) given to the month field.
            encryptedExpiryDate: getErrorField(stateOnChange, 'encryptedExpiryMonth') ??
                getErrorField(stateOnChange, 'encryptedExpiryYear'),
        },
    };
}
function isAdyenStateValid(state) {
    return (state.isValid &&
        state.paymentMethod.type !== '' &&
        state.paymentMethod.encryptedCardNumber !== '' &&
        state.paymentMethod.encryptedExpiryMonth !== '' &&
        state.paymentMethod.encryptedExpiryYear !== '' &&
        state.paymentMethod.encryptedSecurityCode !== '' &&
        state.paymentMethod.holderName !== '');
}
function convertAdyenState(state) {
    return {
        ...state.paymentMethod,
        storeInVault: state.storeInVault,
        creditCardBrand: state.creditCardBrand,
        lastFour: state.lastFour,
    };
}
// We have a field error from Adyen only if the customer actually touched the related input.
// So 'no error' means that either the field is valid or empty: if empty, we had the requiredMessage.
function addRequiredMessageIfNeeded(errors, state, requiredMessage) {
    const newErrors = {};
    if (errors.encryptedCardNumber === null && state.paymentMethod.encryptedCardNumber === '') {
        newErrors.encryptedCardNumber = requiredMessage;
    }
    if (errors.holderName === null && state.paymentMethod.holderName === '') {
        newErrors.holderName = requiredMessage;
    }
    if (errors.encryptedExpiryDate === null &&
        (state.paymentMethod.encryptedExpiryMonth === '' ||
            state.paymentMethod.encryptedExpiryYear === '')) {
        newErrors.encryptedExpiryDate = requiredMessage;
    }
    if (errors.encryptedSecurityCode === null && state.paymentMethod.encryptedSecurityCode === '') {
        newErrors.encryptedSecurityCode = requiredMessage;
    }
    return newErrors;
}
/*
 * Other parsing utilities
 */
function getLastFourDigits(data) {
    if (_get(data, 'fieldType') === 'encryptedCardNumber') {
        const endDigits = _get(data, 'endDigits');
        return typeof endDigits === 'string' || typeof endDigits === 'number' ? String(endDigits) : '';
    }
    return null;
}
function getFromAdyenCardBrand(brandData) {
    return normalizeAdyenCardBrand(_get(brandData, 'brand', brandData));
}
function getFocusedElement(data) {
    const focusedElement = _get(data, 'currentFocusObject');
    return typeof focusedElement === 'string' ? focusedElement : null;
}

export { DEFAULT_ADYEN_STATE, DEFAULT_CARD_ERRORS, DEFAULT_ERRORS, addRequiredMessageIfNeeded, areIdenticalCardErrors, convertAdyenState, getErrorField, getFocusedElement, getFromAdyenCardBrand, getLastFourDigits, getPaymentMethodField, isAdyenStateValid, isHolderNameValid, maybeErrorForHolderName, parseStateOnChange };
//# sourceMappingURL=useAdyenState.static.js.map
